import React, {useState} from 'react';
import {
  BookOpenIcon, PlayCircleIcon, Squares2X2Icon, ClockIcon,
} from '@heroicons/react-v2/24/outline';
import cx from 'classnames';

import LessonCard from './LessonCard';
import ModuleCard from './ModuleCard';
import {Badge} from '../../components/Badge';
import {isNewCourse, formatMinutes} from '../../util';

const getMetadata = (course) => {
  const lessonCount = (course.lessons || []).length;
  const videoCount = (course.lessons || []).filter((l) => !!l.video).length;
  const {estimatedTime} = course;

  const lessonString = lessonCount === 1 ? `${lessonCount} lesson` : `${lessonCount} lessons`;
  const videoString = videoCount === 1 ? `${videoCount} video` : `${videoCount} videos`;

  const timeString = formatMinutes(estimatedTime);

  return {
    lessonCount,
    lessonString,
    videoCount,
    videoString,
    estimatedTime,
    timeString,
  };
};

const CoursePanel = ({
  course,
  truncateAfter,
  trackSlug = null,
  onClickLesson,
}) => {
  const [showAll, setShowAll] = useState(false);
  const metadata = getMetadata(course);
  const courseIsNew = isNewCourse(course);

  return (
    <div
      id={course.slug}
      key={course.slug}
      name={course.slug}
      className="flex flex-col lg:border border-gray-100 lg:shadow px-6 lg:px-10 lg:pt-8 pb-10 bg-white rounded-xl"
    >

      <div className="flex justify-between mt-1">
        <h3 className="inline text-lg lg:text-2xl font-bold text-gray-900 leading-tight">{course.shortTitle}</h3>
        {courseIsNew && !course.comingSoon && (
          <Badge className="px-4 py-1 flex items-center whitespace-nowrap h-8">New</Badge>
        )}
        {course.comingSoon && (
          <Badge className="px-4 py-1 flex items-center whitespace-nowrap h-8">Coming soon</Badge>
        )}
      </div>

      {!course.comingSoon && metadata.lessonCount > 0 && (
        <div className="flex mt-2 text-gray-500 font-regular text-sm space-x-3">
          {course.modules?.length > 0 && (
            <div className="flex items-center space-x-1">
              <Squares2X2Icon className="h-5 w-5" />
              <span>{course.modules.length > 1 ? `${course.modules.length} modules` : '1 module'}</span>
            </div>
          )}
          <div className="flex items-center space-x-1">
            <BookOpenIcon className="h-5 w-5" />
            <span>{metadata.lessonString}</span>
          </div>
          {metadata.videoCount > 0 && (
            <div className="flex items-center space-x-1">
              <PlayCircleIcon className="h-5 w-5" />
              <span>{metadata.videoString}</span>
            </div>
          )}
          {metadata.estimatedTime > 0 && ( // hidden on mobile due to space constraints
            <div className="hidden sm:flex flex items-center space-x-1">
              <ClockIcon className="h-5 w-5" />
              <span>{metadata.timeString}</span>
            </div>
          )}
        </div>
      )}

      <span className="mt-4 text-base font-regular text-gray-500 max-w-xl">{course.description}</span>

      {!course.comingSoon && course.lessons?.length > 0 && (
        <>
          <div className="flex flex-col justify-center mt-5 border border-gray-200 shadow-xs rounded-lg transition duration-50 overflow-hidden">
            {course.modules?.length > 0 && course.modules.map((module, idx) => (
              <ModuleCard
                key={module.slug}
                module={module}
                courseSlug={course.slug}
                courseIsNew={courseIsNew}
                trackSlug={trackSlug}
                onClickLesson={onClickLesson}
                index={idx}
              />
            ))}
            {!course.modules?.length && course.lessons.map((lesson, idx) => (
              <LessonCard
                key={lesson.slug}
                lesson={lesson}
                courseSlug={course.slug}
                courseIsNew={courseIsNew}
                trackSlug={trackSlug}
                onClickLesson={onClickLesson}
                className={cx({hidden: !showAll && truncateAfter && idx >= truncateAfter})}
              />
            ))}
          </div>

          {(truncateAfter && !course.modules?.length && course.lessons.length > truncateAfter) && (
            <button
              type="button"
              className="inline-block mx-auto mt-4 rounded-full bg-indigo-100 hover:bg-indigo-100 px-4 py-1 font-medium text-indigo-600"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? 'Collapse' : `Show all ${course.lessons.length} lessons`}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default CoursePanel;
